// scormApi.js — API SCORM 1.2 que un paquete (Articulate Storyline, etc.) busca
// en la ventana que lo contiene: window.API con LMSInitialize, LMSGetValue,
// LMSSetValue, LMSCommit, LMSFinish y los de error.
//
// Guarda el modelo de datos en memoria y llama a `alGuardar(cmi, { fin })` en
// cada LMSCommit/LMSFinish (y al cerrar). Interactivos fase 1.
//
// `inicial`: valores con los que arranca (alumno, y en la fase 3 la ubicación
// y el estado interno para retomar).

const SOLO_LECTURA = new Set([
  'cmi.core._children', 'cmi.core.student_id', 'cmi.core.student_name', 'cmi.core.credit', 'cmi.core.entry',
  'cmi.core.total_time', 'cmi.core.lesson_mode', 'cmi.launch_data', 'cmi.comments_from_lms',
  'cmi.core.score._children', 'cmi.objectives._children', 'cmi.objectives._count', 'cmi.interactions._children', 'cmi.interactions._count',
  'cmi.student_data._children', 'cmi.student_preference._children', 'cmi.student_data.mastery_score',
]);
const ERRORES = { 0: 'No error', 101: 'General exception', 201: 'Invalid argument error', 301: 'Not initialized', 401: 'Not implemented error', 403: 'Element is read only', 404: 'Element is write only' };

export const crearApiScorm12 = ({ inicial = {}, alGuardar, alCambiar } = {}) => {
  const cmi = {
    'cmi.core._children': 'student_id,student_name,lesson_location,credit,lesson_status,entry,score,total_time,lesson_mode,exit,session_time',
    'cmi.core.score._children': 'raw,min,max',
    'cmi.core.student_id': '', 'cmi.core.student_name': '', 'cmi.core.lesson_location': '', 'cmi.core.credit': 'credit',
    'cmi.core.lesson_status': 'not attempted', 'cmi.core.entry': 'ab-initio', 'cmi.core.score.raw': '', 'cmi.core.score.min': '',
    'cmi.core.score.max': '', 'cmi.core.total_time': '0000:00:00', 'cmi.core.lesson_mode': 'normal', 'cmi.core.exit': '',
    'cmi.core.session_time': '', 'cmi.suspend_data': '', 'cmi.launch_data': '', 'cmi.comments': '', 'cmi.comments_from_lms': '',
    'cmi.objectives._children': 'id,score,status', 'cmi.objectives._count': '0',
    'cmi.interactions._children': 'id,objectives,time,type,correct_responses,weighting,student_response,result,latency', 'cmi.interactions._count': '0',
    'cmi.student_data._children': 'mastery_score,max_time_allowed,time_limit_action', 'cmi.student_data.mastery_score': '',
    'cmi.student_preference._children': 'audio,language,speed,text',
    ...inicial,
  };
  let iniciado = false;
  let terminado = false;
  let ultimoError = 0;
  let sucio = false;

  const guardar = (fin) => {
    if (!sucio && !fin) return 'true';
    sucio = false;
    try { alGuardar?.({ ...cmi }, { fin }); } catch (e) { /* nunca romper el paquete */ }
    return 'true';
  };

  const api = {
    LMSInitialize: () => { if (terminado) { ultimoError = 101; return 'false'; } iniciado = true; ultimoError = 0; return 'true'; },
    LMSFinish: () => {
      if (!iniciado) { ultimoError = 301; return 'false'; }
      ultimoError = 0;
      guardar(true);
      iniciado = false; terminado = true;
      return 'true';
    },
    LMSGetValue: (k) => {
      if (!iniciado) { ultimoError = 301; return ''; }
      ultimoError = 0;
      if (k === 'cmi.core.exit' || k === 'cmi.core.session_time') { ultimoError = 404; return ''; }
      return Object.prototype.hasOwnProperty.call(cmi, k) ? String(cmi[k]) : (/^cmi\./.test(k) ? '' : (ultimoError = 201, ''));
    },
    LMSSetValue: (k, v) => {
      if (!iniciado) { ultimoError = 301; return 'false'; }
      if (SOLO_LECTURA.has(k)) { ultimoError = 403; return 'false'; }
      if (!/^cmi\./.test(k)) { ultimoError = 201; return 'false'; }
      ultimoError = 0;
      const valor = String(v ?? '');
      // Interacciones y objetivos: llevar la cuenta como pide el estándar.
      const n = k.match(/^cmi\.(interactions|objectives)\.(\d+)\./);
      if (n) { const c = `cmi.${n[1]}._count`; cmi[c] = String(Math.max(Number(cmi[c] || 0), Number(n[2]) + 1)); }
      if (cmi[k] !== valor) { cmi[k] = valor; sucio = true; alCambiar?.(k, valor); }
      return 'true';
    },
    LMSCommit: () => { if (!iniciado) { ultimoError = 301; return 'false'; } ultimoError = 0; return guardar(false); },
    LMSGetLastError: () => String(ultimoError),
    LMSGetErrorString: (c) => ERRORES[Number(c)] || 'Unknown error',
    LMSGetDiagnostic: (c) => ERRORES[Number(c ?? ultimoError)] || '',
    /** Para el reproductor: cerrar al salir de la lección aunque el paquete no llame a LMSFinish. */
    _cerrar: () => { if (iniciado) { api.LMSFinish(); } },
    _cmi: () => ({ ...cmi }),
  };
  return api;
};
